<div class="view-container">
  <mat-card class="order-split-card">
    <div class="order-split-card__header">
      <div class="order-split-card__title-row">
        <div class="order-split-card__icon">
          <mat-icon>call_split</mat-icon>
        </div>
        <div>
          <h1 class="order-split-card__title">Split order</h1>
          @if (order) {
            <p class="order-split-card__subtitle">Order #{{ order.id }}</p>
          }
        </div>
      </div>
    </div>

    <mat-stepper linear #stepper class="order-split-card__stepper">
      <mat-step [editable]="isEditable" [stepControl]="orderSplitForm">
        <form [formGroup]="orderSplitForm" class="order-split-card__step">
          <ng-template matStepLabel>Number of orders</ng-template>

          <p class="order-split-card__intro">
            Enter how many orders this prescription should be split into. Each split order must
            contain at least one medicine item.
          </p>

          <mat-form-field appearance="outline" class="order-split-card__field">
            <mat-label>Number of orders</mat-label>
            <input min="2" type="number" matInput formControlName="numberOfOrders" />
            @if (orderSplitForm.get('numberOfOrders')?.invalid) {
              <mat-error>Enter at least 2 orders</mat-error>
            }
          </mat-form-field>

          <div class="order-split-card__footer">
            <button
              type="button"
              class="order-split-card__btn--primary"
              [disabled]="!orderSplitForm.valid"
              mat-flat-button
              color="primary"
              (click)="split()"
            >
              <mat-icon>chevron_right</mat-icon> Next
            </button>
          </div>
        </form>
      </mat-step>

      <mat-step [editable]="isEditable">
        <ng-template matStepLabel>Distribute order items</ng-template>

        <p class="order-split-card__intro">
          Drag medicines between orders to distribute items. Each order must keep at least one item.
        </p>

        <div class="order-split-card__splits">
          @for (split of splits; track split) {
            <mat-card class="order-split-card__split-card">
              <mat-card-title class="order-split-card__split-title">
                Order <strong>#{{ split.id }}</strong>
              </mat-card-title>
              <mat-divider></mat-divider>
              <mat-accordion
                [@.disabled]="disableAnimation"
                multi="true"
                cdkDropList
                id="{{ split.id }}"
                [cdkDropListData]="split.orderItems"
                [cdkDropListConnectedTo]="connectedTo"
                class="order-split-card__item-list"
                (cdkDropListDropped)="drop($event)"
              >
                @for (item of split.orderItems; track item) {
                  <mat-expansion-panel [expanded]="false" cdkDrag class="order-split-card__panel">
                    <mat-expansion-panel-header>
                      <mat-panel-title>
                        {{ item.medicine.name }}
                      </mat-panel-title>
                      <mat-panel-description>
                        {{ item.dosageType.additional_information }} —
                        {{ item.status ? item.status.status : '—' }}
                      </mat-panel-description>
                    </mat-expansion-panel-header>
                    <div class="order-split-card__panel-content">
                      <div class="order-split-card__detail-row">
                        <span class="order-split-card__detail-label">Pack size</span>
                        <span class="order-split-card__detail-value">
                          {{ item.medicineDosagePackageType.contained_quantity }}
                        </span>
                      </div>
                      <div class="order-split-card__detail-row">
                        <span class="order-split-card__detail-label">Quantity</span>
                        <span class="order-split-card__detail-value">{{ item.quantity }}</span>
                      </div>
                      <div class="order-split-card__detail-row">
                        <span class="order-split-card__detail-label">Dosage type</span>
                        <span class="order-split-card__detail-value">
                          {{ item.dosageType.additional_information }}
                        </span>
                      </div>
                      <div class="order-split-card__detail-row">
                        <span class="order-split-card__detail-label">Status</span>
                        <span class="order-split-card__detail-value">
                          {{ item.status ? item.status.status : '—' }}
                        </span>
                      </div>
                    </div>
                  </mat-expansion-panel>
                }
              </mat-accordion>
            </mat-card>
          }
        </div>

        <div class="order-split-card__footer">
          <button
            type="button"
            class="order-split-card__btn"
            mat-stroked-button
            matStepperPrevious
          >
            <mat-icon>chevron_left</mat-icon> Back
          </button>
          <button
            type="button"
            class="order-split-card__btn--primary"
            [disabled]="!isValid"
            mat-flat-button
            color="primary"
            (click)="save()"
          >
            <mat-icon>save</mat-icon> Save
          </button>
        </div>
      </mat-step>

      <mat-step [editable]="isEditable">
        <ng-template matStepLabel>Done</ng-template>

        <p class="order-split-card__intro">
          The order has been split successfully. Open each new order to review the details.
        </p>

        <div class="order-split-card__results">
          @for (newOrder of newOrders; track newOrder) {
            <mat-card class="order-split-card__split-card order-split-card__result-card">
              <mat-card-title class="order-split-card__split-title">
                Order <strong>#{{ newOrder.id }}</strong>
              </mat-card-title>
              <mat-divider></mat-divider>
              <mat-card-content>
                <mat-accordion multi="true" class="order-split-card__results-accordion">
                  @for (item of newOrder.order_items; track item) {
                    <mat-expansion-panel [expanded]="true" class="order-split-card__panel">
                      <mat-expansion-panel-header>
                        <mat-panel-title>
                          {{ item.medicine_dosage_package_type.medicine_dosage_type.medicine.name }}
                        </mat-panel-title>
                        <mat-panel-description>
                          {{ item.medicine_dosage_package_type.medicine_dosage_type.additional_information }}
                          — {{ item.status.status }}
                        </mat-panel-description>
                      </mat-expansion-panel-header>
                      <div class="order-split-card__panel-content">
                        <div class="order-split-card__detail-row">
                          <span class="order-split-card__detail-label">Pack size</span>
                          <span class="order-split-card__detail-value">
                            {{ item.medicine_dosage_package_type.contained_quantity }}
                          </span>
                        </div>
                        <div class="order-split-card__detail-row">
                          <span class="order-split-card__detail-label">Quantity</span>
                          <span class="order-split-card__detail-value">{{ item.total_quantity }}</span>
                        </div>
                        <div class="order-split-card__detail-row">
                          <span class="order-split-card__detail-label">Dosage type</span>
                          <span class="order-split-card__detail-value">
                            {{ item.medicine_dosage_package_type.medicine_dosage_type.additional_information }}
                          </span>
                        </div>
                        <div class="order-split-card__detail-row">
                          <span class="order-split-card__detail-label">Status</span>
                          <span class="order-split-card__detail-value">{{ item.status.status }}</span>
                        </div>
                      </div>
                    </mat-expansion-panel>
                  }
                </mat-accordion>
              </mat-card-content>
              <mat-card-actions align="end" class="order-split-card__result-actions">
                <button
                  type="button"
                  class="order-split-card__btn"
                  mat-stroked-button
                  (click)="openOrder(newOrder)"
                >
                  <mat-icon>open_in_new</mat-icon> Open
                </button>
              </mat-card-actions>
            </mat-card>
          }
        </div>
      </mat-step>
    </mat-stepper>
  </mat-card>
</div>
